import { useState } from 'react'
import Button from '../Button'
import { CandidateResult } from '../../api/role/models/searchOnJobRelevancy/SearchOnJobRelevancyRes'
import { PersonLiteDto } from '../../api/person/models/PersonDto'
import { SetExtra } from '../../utils/SetExtra'
import roleApi from '../../api/role/RoleApi'
import StringList from './StringList'
import LoadingSpinner from '../LoadingSpinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { toast, Toaster } from 'sonner'
import personApi from '../../api/person/PersonApi'
import Section from '../Section'
import PersonUi from '../PersonUi'
import MatchScore from '../MatchScore'
import RankingScore from '../RankingScore'

export default function FullRanking(): React.JSX.Element {
    const [jobTitles, setJobTitles] = useState<string[]>([
        'QA',
        'Test Engineer',
    ])
    const [searchPhrases, setSearchPhrases] = useState<string[]>([
        'Test automation',
        'Selenium',
        'Worked in enterprise businesses in energy, telecoms, or finance',
    ])
    const [results, setResults] = useState<CandidateResult[]>([])
    const [personLites, setPersonLites] = useState<PersonLiteDto[]>([])
    const [isLoading, setIsLoading] = useState(false)

    async function search() {
        setIsLoading(true)
        setResults([])
        setPersonLites([])

        try {
            const candidatesIds = new SetExtra<string>()
            const res = await roleApi.searchOnJobRelevancy(
                searchPhrases,
                jobTitles,
            )
            res.results.forEach((result) => {
                candidatesIds.add(result.id)
            })

            const getPersonsLiteRes = await personApi.getPersonsLite({
                personIds: candidatesIds.getValuesAsArray(),
            })

            setPersonLites(getPersonsLiteRes.persons)
            setResults(res.results)
        } catch (err) {
            console.error(err)
            toast.error(
                'There was an error when searching candidates, please try again',
                {
                    duration: 5000,
                },
            )
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <StringList
                addButtonText="Add Job Title"
                value={jobTitles}
                onChange={setJobTitles}
            />
            <StringList
                className="mt-4"
                addButtonText="Add Search Phrase"
                value={searchPhrases}
                onChange={setSearchPhrases}
            />
            <Button
                onClick={search}
                type="button"
                style="primary"
                classes="mt-4"
                enabled={!isLoading}
            >
                {isLoading && <LoadingSpinner spinnerSize={24} />}
                {!isLoading && <FontAwesomeIcon icon={faMagnifyingGlass} />}
                <div className="ml-2">Search</div>
            </Button>
            {results.length > 0 && personLites.length > 0 ? (
                <Section title="Search Results" className="mt-4">
                    {results.map((result) => {
                        const person = personLites.find(
                            (personLite) => personLite.id === result.id,
                        )

                        if (!person) {
                            return null
                        }

                        return (
                            <div
                                key={result.id}
                                className="candidate-result mb-6"
                            >
                                <div className="flex">
                                    <PersonUi person={person} />
                                    <MatchScore
                                        value={result.score * 100}
                                        className="mt-2"
                                    />
                                </div>
                                <Section
                                    title="Search Details"
                                    titleType="h2"
                                    collapsible={true}
                                    defaultCollapsed={false}
                                >
                                    <div className="mt-2 flex items-center">
                                        <MatchScore
                                            className="mr-2"
                                            variant="icon"
                                            value={
                                                result.details!
                                                    .titleSimilarity * 100
                                            }
                                        />
                                        Best title: {result.details?.title}
                                    </div>
                                    {result.phraseScores.map(
                                        (phraseScore, index) => (
                                            <div
                                                key={`${result.id}_phraseScore_${index}`}
                                                className="mt-2 flex items-center"
                                            >
                                                <MatchScore
                                                    className="mr-2"
                                                    variant="icon"
                                                    value={
                                                        phraseScore.score * 100
                                                    }
                                                />
                                                {phraseScore.phrase}
                                            </div>
                                        ),
                                    )}
                                </Section>
                                <Section
                                    title="Ranking Details"
                                    titleType="h2"
                                    collapsible={true}
                                    defaultCollapsed={true}
                                >
                                    {result.details?.searchPhraseScores.searchPhraseScores.map(
                                        (phraseScore, index) => (
                                            <div
                                                key={`${result.id}_details_${index}`}
                                                className="mt-2 border p-2 rounded-lg"
                                            >
                                                <div className="font-bold">
                                                    {phraseScore.phrase}
                                                </div>
                                                <div>
                                                    <RankingScore
                                                        value={
                                                            phraseScore.score *
                                                            100
                                                        }
                                                    />
                                                </div>
                                                <div>
                                                    {phraseScore.data_type}
                                                </div>
                                                <div>{phraseScore.text}</div>
                                            </div>
                                        ),
                                    )}
                                </Section>
                            </div>
                        )
                    })}
                </Section>
            ) : (
                <></>
            )}
            <Toaster />
        </div>
    )
}
